// Generated by Copilot- Start
import { useCallback, useMemo, useState } from "react";
import { ChannelCardV2 } from "lib/helper/channel-interactive-tool-v2";
// [WAT-11791] Same label helper the search box, chips and dropdown use, so "which channel is this"
// is answered identically on every surface.
import { getChoiceKey, getChoiceLabel } from "src/components/video-lab/ChannelSearch/utils/choice-key";

/**
 * [PR #8099 review] The broadcast station behind a card, when the feed names one.
 *
 * Read from `rawData` rather than promoted to a top-level `callSign` on purpose. The value is
 * already there — the searchable-channels builder passes the provider's object straight through as
 * `rawData`, and `LocalChannelModalV2` already reads it this way — while SETTING a top-level
 * `callSign` on these cards would newly feed two package-matching lookups that key on exactly that
 * field (`package-matching.ts:220` and `:295`). Adding a field to fix a comparison is not worth
 * turning on an unrelated code path.
 *
 * Nationals carry no call sign and need none: a national is the same station in every market, so it
 * has nothing market-scoped to replace. They compare equal here and keep their existing object.
 */
const stationOf = (channel: any): string => String(channel?.callSign ?? channel?.rawData?.callSign ?? "");

export interface SearchState {
  selectedChannels: ChannelCardV2[];
  selectedSvods: any[];
  selectedShows: any[];
}

export interface SearchHandlers {
  selectedChannels: ChannelCardV2[];
  selectedSvods: any[];
  selectedShows: any[];
  updateSearch: (selectedData: any[]) => void;
  /** [WAT-11791] Replace/drop the selected channels for a newly chosen market. */
  refreshSelectedChannelsForMarket: (marketChannels: ChannelCardV2[] | undefined) => void;
  handleShowSearch: () => boolean;
  searchedChannelsByCategory: Record<string, ChannelCardV2[]>;
  searchedCategories: Array<{ key: string; value: string }>;
  searchedAddonChannelsByCategory: Record<string, ChannelCardV2[]>; // NEW: Addon channels separated
  searchedAddonCategories: Array<{ key: string; value: string }>; // NEW: Categories with addons
}

export const useSearchHandlers = (
  allCategories: Array<{ key: string; value: string }> | undefined,
  showGenrePackages: boolean | undefined,
  showMyFreeDTVChannels: boolean | undefined,
  enableSearch: boolean | undefined,
  allAddOnChannels?: string[] // NEW: List of addon channel names
): SearchHandlers => {
  const [selectedChannels, setSelectedChannels] = useState<ChannelCardV2[]>([]);
  const [selectedSvods, setSelectedSvods] = useState<any[]>([]);
  const [selectedShows, setSelectedShows] = useState<any[]>([]);

  const updateSearch = useCallback((selectedData: any[]) => {
    if (!selectedData || selectedData.length === 0) {
      setSelectedChannels([]);
      setSelectedSvods([]);
      setSelectedShows([]);
      return;
    }

    const tempSvodData: any[] = [];
    const resourceIdData: any[] = [];
    const otherData: any[] = [];

    selectedData.forEach(item => {
      if (item?.resourceId) {
        resourceIdData.push(item);
      } else if (item?.svodData) {
        tempSvodData.push(item);
      } else {
        otherData.push(item);
      }
    });

    setSelectedShows(resourceIdData);
    setSelectedSvods(tempSvodData);
    setSelectedChannels(otherData);
  }, []);

  // [WAT-11791] Refresh the selected CHANNELS for a new market.
  //
  // This surface is different from the channel-lineup page and the modal: there the selection is
  // matched AGAINST a row list, so re-pointing its identity is enough. Here the selected objects
  // ARE what gets rendered, so a stale one keeps the previous market's channel number, logo and
  // package data on screen. `updateSearch` only runs when the customer changes the selection and
  // the zipcode handler never touched it, so after a ZIP change the tool went on displaying
  // MyNetworkTV in a market that carries no MyNetworkTV — while the search dropdown, rebuilt from
  // the new market, correctly stopped offering it.
  //
  // So this REPLACES rather than re-stamps, and it DROPS what the new market lacks. Dropping is
  // right here and wrong on the page: the page's "not available" message is only reachable while
  // a chip stays selected and matches nothing, whereas here rendering a channel the market does
  // not carry IS the defect.
  //
  // Subscriptions and shows are untouched — neither is DMA-scoped.
  const refreshSelectedChannelsForMarket = useCallback((marketChannels: ChannelCardV2[] | undefined) => {
    if (!Array.isArray(marketChannels)) {
      return;
    }
    setSelectedChannels(prev => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return prev;
      }
      // 🔴 [PR #8099 review] An earlier version keyed this map to ONE channel per label and kept
      // the first. That was a real bug on the very shape this ticket family is about: with two
      // same-named stations selected (WAT-11767 makes that reachable), BOTH selections resolved to
      // the same first object after a ZIP change — one identity silently lost and its card
      // rendered twice. It also quietly GUESSED a station, which is exactly what the page
      // reconciler refuses to do.
      //
      // Keep every candidate per label instead, and never guess: an unresolvable selection expands
      // to all of the new market's stations of that name, which is what the page shows for the
      // same input (it strips the identity and lets the exact-name match return both rows).
      const byKey = new Map<string, ChannelCardV2>();
      const byLabel = new Map<string, ChannelCardV2[]>();
      for (const channel of marketChannels) {
        // Keyed BEFORE the label guard below, because a row with no usable name still has a key and
        // still counts as carried by this market — that was true of the previous `liveKeys` set and
        // must stay true. First wins: two rows with one key are indistinguishable by key anyway.
        const channelKey = getChoiceKey(channel as any);
        if (!byKey.has(channelKey)) {
          byKey.set(channelKey, channel);
        }
        // [PR #8099 review] No `?.` chain on this call, deliberately: `getChoiceLabel` is declared
        // `=> string` and builds its result through `str(...)` (choice-key.ts:77), so the value
        // cannot be nullish here and the team's defensive-guard rule yields to the type-aware
        // check at this site. Same at the selection loop below.
        const label = getChoiceLabel(channel as any).trim().toLowerCase();
        if (!label) {
          continue;
        }
        const sameName = byLabel.get(label);
        if (sameName) {
          sameName.push(channel);
        } else {
          byLabel.set(label, [channel]);
        }
      }

      const next: ChannelCardV2[] = [];
      const pushedKeys = new Set<string>();
      const expandedLabels = new Set<string>();
      let changed = false;
      const push = (channel: ChannelCardV2) => {
        const key = getChoiceKey(channel as any);
        if (pushedKeys.has(key)) {
          return; // a live selection and an expanded label can name the same station
        }
        pushedKeys.add(key);
        next.push(channel);
      };

      for (const selection of prev) {
        // Still carried by this market under the same key. That stops a same-market rebuild from
        // expanding a deliberate one-of-two pick into both — but the key alone does NOT prove it is
        // the same STATION. Two markets can carry the same network on the same channel number, and
        // `label|chnlNum` cannot separate those, so the card kept here would be the previous
        // market's logo, packages and raw record. Take the MARKET's object instead.
        //
        // 🔴 The change is reported BY VALUE, not by reference, and that is the whole design:
        // `marketChannels` is rebuilt from scratch whenever its memo inputs change, and this runs
        // from an effect on that list, so a reference comparison would report a change on a plain
        // re-render and loop. Comparing the station is stable (a same-market rebuild yields the same
        // call sign, so `changed` stays false and `prev` is returned untouched) and idempotent (once
        // the market object IS the selection, the next pass compares equal).
        const live = byKey.get(getChoiceKey(selection as any));
        if (live) {
          if (stationOf(live) !== stationOf(selection)) {
            changed = true;
          }
          push(live);
          continue;
        }
        const label = getChoiceLabel(selection as any).trim().toLowerCase();
        const candidates = label ? byLabel.get(label) : undefined;
        if (!candidates?.length) {
          changed = true; // absent from this market — drop it
          continue;
        }
        changed = true;
        if (expandedLabels.has(label)) {
          continue; // a sibling selection of the same name already brought these in
        }
        expandedLabels.add(label);
        candidates.forEach(push);
      }
      // Same-reference contract, so a caller wiring this to an effect cannot loop.
      return changed ? next : prev;
    });
  }, []);

  const handleShowSearch = useCallback(() => {
    if (showGenrePackages || showMyFreeDTVChannels) {
      return false;
    }
    return enableSearch !== false;
  }, [showGenrePackages, showMyFreeDTVChannels, enableSearch]);

  const searchedChannelsByCategory = useMemo(() => {
    if (selectedChannels.length === 0) {
      return {};
    }

    // Separate regular channels from addon channels
    const regularChannels = selectedChannels.filter(channel => {
      // Check if channel is in allAddOnChannels list
      const isAddon = allAddOnChannels?.includes(channel.label);
      return !isAddon;
    });

    return regularChannels.reduce((acc, channel) => {
      const categoryId = channel.categoryId || "OTHER";
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(channel);
      return acc;
    }, {} as Record<string, ChannelCardV2[]>);
  }, [selectedChannels, allAddOnChannels]);

  // NEW: Separate addon channels by category
  const searchedAddonChannelsByCategory = useMemo(() => {
    if (selectedChannels.length === 0) {
      return {};
    }

    // Filter only addon channels
    const addonChannels = selectedChannels.filter(channel => {
      const isAddon = allAddOnChannels?.includes(channel.label);
      return isAddon;
    });

    return addonChannels.reduce((acc, channel) => {
      const categoryId = channel.categoryId || "OTHER";
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(channel);
      return acc;
    }, {} as Record<string, ChannelCardV2[]>);
  }, [selectedChannels, allAddOnChannels]);

  const searchedCategories = useMemo(() => {
    if (!allCategories || selectedChannels.length === 0) {
      return [];
    }

    const categoryIds = Object.keys(searchedChannelsByCategory);
    return allCategories.filter(cat => categoryIds.includes(cat.key));
  }, [allCategories, searchedChannelsByCategory, selectedChannels.length]);

  // NEW: Categories that have addon channels
  const searchedAddonCategories = useMemo(() => {
    if (!allCategories || selectedChannels.length === 0) {
      return [];
    }

    const categoryIds = Object.keys(searchedAddonChannelsByCategory);
    return allCategories.filter(cat => categoryIds.includes(cat.key));
  }, [allCategories, searchedAddonChannelsByCategory, selectedChannels.length]);

  return {
    selectedChannels,
    selectedSvods,
    selectedShows,
    updateSearch,
    refreshSelectedChannelsForMarket,
    handleShowSearch,
    searchedChannelsByCategory,
    searchedCategories,
    searchedAddonChannelsByCategory,
    searchedAddonCategories
  };
};
// Generated by Copilot- End
